import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import SiteLayout from '@/components/feature/SiteLayout';
import PageHero from '@/components/feature/PageHero';
import CtaSection from '@/components/feature/CtaSection';
import Reveal from '@/components/base/Reveal';
import Counter from '@/components/base/Counter';
import SectionHeading from '@/components/base/SectionHeading';
import ReportCard from '@/pages/actualites/components/ReportCard';
import SessionsTable from '@/pages/actualites/components/SessionsTable';
import { newsFigures, siteReports, trainingSessions } from '@/mocks/actualites';
import { company } from '@/mocks/site';
import usePageMeta from '@/hooks/usePageMeta';

const allSectors = 'Tous les chantiers';

export default function Actualites() {
  usePageMeta(
    'Actualités, comptes rendus de chantier et sessions | SGSCTIC',
    'Comptes rendus de chantier, nouvelles de l’atelier et calendrier des prochaines sessions de formation SGSCTIC à Douala : suivez la vie de nos équipes et inscrivez-vous.',
  );
  const [activeSector, setActiveSector] = useState(allSectors);

  const sectors = useMemo(
    () => [allSectors, ...Array.from(new Set(siteReports.map((report) => report.sector)))],
    [],
  );

  const filtered = useMemo(
    () =>
      activeSector === allSectors
        ? siteReports
        : siteReports.filter((report) => report.sector === activeSector),
    [activeSector],
  );

  return (
    <SiteLayout>
      <PageHero
        breadcrumb="Actualités"
        label="Journal de l’atelier"
        title="Ce qui se passe sur nos chantiers"
        description="Comptes rendus de chantier, étapes marquantes et calendrier des prochaines sessions de formation : la vie de SGSCTIC, sans filtre marketing."
        image="https://sgsctic.com/wp-content/uploads/2026/01/WhatsApp-Image-2025-12-19-at-10.39.37-1.jpeg"
        imageAlt="Soudeur SGSCTIC en poste dans l’atelier de Logbaba"
      />

      {/* Figures */}
      <section className="border-b border-background-200 bg-background-100 py-10 md:py-12">
        <div className="container-wide">
          <div className="grid gap-px overflow-hidden rounded-lg bg-background-200 sm:grid-cols-3">
            {newsFigures.map((figure) => (
              <div key={figure.label} className="bg-background-50 px-5 py-6">
                <p className="font-heading text-4xl font-semibold leading-none text-foreground-950">
                  <Counter value={figure.value} suffix={figure.suffix} />
                </p>
                <p className="mt-2 text-[10px] uppercase leading-snug tracking-[0.14em] text-foreground-600">
                  {figure.label}
                </p>
                <p className="mt-1 text-[11px] leading-snug text-foreground-400">{figure.hint}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Comptes rendus */}
      <section id="comptes-rendus" className="scroll-mt-24 bg-background-50 py-16 md:py-24">
        <div className="container-wide">
          <Reveal>
            <SectionHeading
              label="Comptes rendus de chantier"
              title="Ce que nous avons livré ces derniers mois"
              description="Chaque chantier terminé fait l’objet d’un compte rendu court : le contexte, ce qui a été réalisé, et les points vérifiés avant la réception."
            />
          </Reveal>

          <Reveal delay={90}>
            <div className="mt-9 flex flex-wrap items-center gap-2">
              {sectors.map((sector) => {
                const isActive = sector === activeSector;
                return (
                  <button
                    key={sector}
                    type="button"
                    onClick={() => setActiveSector(sector)}
                    aria-pressed={isActive}
                    className={`whitespace-nowrap rounded-full border px-4 py-2.5 text-[11px] uppercase tracking-[0.1em] transition-colors duration-300 ${
                      isActive
                        ? 'border-primary-500 bg-primary-500 text-background-50'
                        : 'border-background-300 bg-background-50 text-foreground-700 hover:border-primary-400 hover:text-foreground-950'
                    }`}
                  >
                    {sector}
                  </button>
                );
              })}
            </div>
          </Reveal>

          <div key={activeSector} className="mt-8 grid gap-5 md:grid-cols-2 lg:grid-cols-3">
            {filtered.map((report, index) => (
              <Reveal key={report.slug} delay={index * 70} className="h-full">
                <ReportCard report={report} />
              </Reveal>
            ))}
          </div>

          <Reveal delay={120}>
            <div className="mt-10 flex flex-col items-start justify-between gap-4 rounded-lg border border-background-200 bg-background-100 p-6 sm:flex-row sm:items-center">
              <div>
                <p className="label-mono text-primary-600">Une question sur un chantier</p>
                <p className="mt-2 max-w-xl text-sm leading-relaxed text-foreground-700">
                  Vous voulez des précisions sur une réalisation, une référence technique ou les
                  conditions d’intervention ? Nous répondons directement.
                </p>
              </div>
              <Link
                to="/contact"
                className="flex items-center gap-2 whitespace-nowrap rounded-md bg-foreground-950 px-5 py-3 text-[11px] font-semibold uppercase tracking-[0.1em] text-background-50 transition-colors hover:bg-primary-600"
              >
                Nous écrire
                <i className="ri-arrow-right-up-line text-sm" />
              </Link>
            </div>
          </Reveal>
        </div>
      </section>

      {/* Prochaines sessions */}
      <section id="sessions" className="scroll-mt-24 bg-background-100 py-16 md:py-24">
        <div className="container-wide">
          <div className="flex flex-col justify-between gap-8 lg:flex-row lg:items-end">
            <Reveal>
              <SectionHeading
                label="Prochaines sessions"
                title={
                  <>
                    Le calendrier
                    <br />
                    de l’atelier
                  </>
                }
                description="Cinq parcours, des groupes de 8 à 12 participants et des sessions qui démarrent en janvier. Les places sont attribuées après un entretien de positionnement."
              />
            </Reveal>
            <Reveal delay={120}>
              <div className="rounded-lg border border-background-300 bg-background-50 p-5 lg:min-w-[260px]">
                <p className="label-mono text-foreground-500">Centre de formation</p>
                <p className="mt-2 text-sm leading-relaxed text-foreground-800">
                  {company.address}
                </p>
                <a
                  href={company.phoneHref}
                  className="mt-3 flex items-center gap-2 whitespace-nowrap text-sm font-semibold text-primary-700 transition-colors hover:text-primary-800"
                >
                  <i className="ri-phone-line" />
                  {company.phone}
                </a>
              </div>
            </Reveal>
          </div>

          <Reveal delay={60}>
            <div className="mt-9">
              <SessionsTable sessions={trainingSessions} />
            </div>
          </Reveal>

          <Reveal delay={120}>
            <div className="mt-6 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
              <p className="flex items-center gap-2 text-[11px] uppercase tracking-[0.12em] text-foreground-500">
                <i className="ri-information-line text-primary-500" />
                Les dates peuvent être ajustées selon le nombre d’inscrits.
              </p>
              <Link
                to="/formation"
                className="group flex items-center gap-2 whitespace-nowrap text-xs font-semibold uppercase tracking-[0.12em] text-primary-700 transition-colors hover:text-primary-800"
              >
                Voir les programmes détaillés
                <i className="ri-arrow-right-line text-sm transition-transform duration-300 group-hover:translate-x-1" />
              </Link>
            </div>
          </Reveal>
        </div>
      </section>

      <CtaSection
        title="Une actualité à partager"
        accent="ou un chantier à lancer ?"
        description="Nous documentons chaque intervention avec photos, relevés et points de contrôle. Parlez-nous de votre site : nous vous dirons comment nous procéderions."
      />
    </SiteLayout>
  );
}